/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchSourceStatus,
  fetchUserAttributes,
  saveUserAttributeMapping,
  fetchSailpointConnection,
  fetchOIMConnection,
  fetchAllSailpointConnections,
  fetchAllOIMConnections,
  saveConnection,
  saveAiAgentConfig,
  fetchDiscoveryStatus,
} from './migrationThunks';

const initialState = {
  activeTab: 0,
  migrationProgress: 0,
  totalConfigObjects: 0,
  migratedConfigObjects: 0,
  migrationResults: {},
  dataMigrationStatus: {},
  connectionDetails: {},
  selectedObjects: [],
  selectedObjectType: null,
  loading: false,
  error: null,
  todoData: null,
  sourceStatus: null,
  discoveryStatus: null,
  sourceStatusLoading: false,
  userAttributes: null,
  userAttributesLoading: false,
  saveMappingLoading: false,
  saveMappingError: null,
  sailpointConnectionOptions: null,
  oimConnectionOptions: null,
  connectionOptionsLoading: false,
  saveConnectionLoading: false,
  saveConnectionError: null,
  aiAgentEnabled: false,
  saveAiAgentLoading: false,
};

const migrationSlice = createSlice({
  name: 'migration',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setMigrationProgress: (state, action) => {
      state.migrationProgress = action.payload;
    },
    setTotalConfigObjects: (state, action) => {
      state.totalConfigObjects = action.payload;
    },
    setMigratedConfigObjects: (state, action) => {
      state.migratedConfigObjects = action.payload;
    },
    setMigrationResult: (state, action) => {
      state.migrationResults = {
        ...state.migrationResults,
        [action.payload.objectId]: action.payload.result,
      };
    },
    clearMigrationResults: (state) => {
      state.migrationResults = {};
    },
    setSelectedObjects: (state, action) => {
      state.selectedObjects = action.payload;
    },
    toggleSelectedObject: (state, action) => {
      const objectId = action.payload;
      const index = state.selectedObjects.indexOf(objectId);
      const newSelectedObjects = [...state.selectedObjects];
      if (index > -1) {
        newSelectedObjects.splice(index, 1);
      } else {
        newSelectedObjects.push(objectId);
      }
      state.selectedObjects = newSelectedObjects;
    },
    clearSelectedObjects: (state) => {
      state.selectedObjects = [];
    },
    setSelectedObjectType: (state, action) => {
      state.selectedObjectType = action.payload;
    },
    clearAllData: (state) => {
      Object.assign(state, {
        activeTab: 0,
        sourceStatus: null,
        discoveryStatus: null,
        migrationProgress: 0,
        totalConfigObjects: 0,
        migratedConfigObjects: 0,
        migrationResults: {},
        dataMigrationStatus: {},
        selectedObjects: [],
        selectedObjectType: null,
        loading: false,
        error: null,
      });
    },
    setDataMigrationStatus: (state, action) => {
      state.dataMigrationStatus = {
        ...state.dataMigrationStatus,
        [action.payload.applicationId]: action.payload.status,
      };
    },
    clearDataMigrationStatus: (state) => {
      state.dataMigrationStatus = {};
    },
    setConnectionDetails: (state, action) => {
      state.connectionDetails = {
        ...state.connectionDetails,
        [action.payload.sourceType]: action.payload.details,
      };
    },
    setDiscoveryCompleted: (state) => {
      state.discoveryStatus = { data: { discoveryCompleted: true } };
      state.sourceStatus = { ...(state.sourceStatus ?? {}), discoveryCompleted: true };
    },
    resetUserAttribute: (state) => {
      state.userAttributes = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSailpointConnection.fulfilled, (state, action) => {
        state.connectionDetails['sailpoint-migration'] = {
          ...state.connectionDetails['sailpoint-migration'],
          ...action.payload?.data,
          status: 'connected',
        };
      })
      .addCase(fetchOIMConnection.fulfilled, (state, action) => {
        state.connectionDetails['oim-migration'] = {
          ...state.connectionDetails['oim-migration'],
          ...action.payload?.data,
          status: 'connected',
        };
      })
      .addCase(fetchSourceStatus.pending, (state) => {
        state.sourceStatusLoading = true;
      })
      .addCase(fetchSourceStatus.fulfilled, (state, action) => {
        state.sourceStatusLoading = false;
        state.sourceStatus = action.payload?.data ?? null;
        state.discoveryStatus = { data: action.payload?.data ?? null };
      })
      .addCase(fetchSourceStatus.rejected, (state, action) => {
        state.sourceStatusLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchDiscoveryStatus.pending, (state) => {
        state.sourceStatusLoading = true;
      })
      .addCase(fetchDiscoveryStatus.fulfilled, (state, action) => {
        state.sourceStatusLoading = false;
        state.sourceStatus = action.payload?.data ?? null;
        state.discoveryStatus = { data: action.payload?.data ?? null };
      })
      .addCase(fetchDiscoveryStatus.rejected, (state, action) => {
        state.sourceStatusLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUserAttributes.pending, (state) => {
        state.userAttributesLoading = true;
      })
      .addCase(fetchUserAttributes.fulfilled, (state, action) => {
        state.userAttributesLoading = false;
        state.userAttributes = action.payload;
      })
      .addCase(fetchUserAttributes.rejected, (state, action) => {
        state.userAttributesLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveUserAttributeMapping.pending, (state) => {
        state.saveMappingLoading = true;
        state.saveMappingError = null;
      })
      .addCase(saveUserAttributeMapping.fulfilled, (state) => {
        state.saveMappingLoading = false;
      })
      .addCase(saveUserAttributeMapping.rejected, (state, action) => {
        state.saveMappingLoading = false;
        state.saveMappingError = action.payload?.message || action.error.message;
      })
      .addCase(fetchAllSailpointConnections.pending, (state) => {
        state.connectionOptionsLoading = true;
      })
      .addCase(fetchAllSailpointConnections.fulfilled, (state, action) => {
        state.connectionOptionsLoading = false;
        state.sailpointConnectionOptions = action.payload?.data || [];
      })
      .addCase(fetchAllSailpointConnections.rejected, (state, action) => {
        state.connectionOptionsLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAllOIMConnections.pending, (state) => {
        state.connectionOptionsLoading = true;
      })
      .addCase(fetchAllOIMConnections.fulfilled, (state, action) => {
        state.connectionOptionsLoading = false;
        state.oimConnectionOptions = action.payload?.data || [];
      })
      .addCase(fetchAllOIMConnections.rejected, (state, action) => {
        state.connectionOptionsLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveConnection.pending, (state) => {
        state.saveConnectionLoading = true;
        state.saveConnectionError = null;
      })
      .addCase(saveConnection.fulfilled, (state) => {
        state.saveConnectionLoading = false;
      })
      .addCase(saveConnection.rejected, (state, action) => {
        state.saveConnectionLoading = false;
        state.saveConnectionError = action.payload?.message || action.error.message;
      })
      .addCase(saveAiAgentConfig.pending, (state) => {
        state.saveAiAgentLoading = true;
      })
      .addCase(saveAiAgentConfig.fulfilled, (state) => {
        state.saveAiAgentLoading = false;
      })
      .addCase(saveAiAgentConfig.rejected, (state) => {
        state.saveAiAgentLoading = false;
      });
  },
});

export const {
  setActiveTab,
  setMigrationProgress,
  setTotalConfigObjects,
  setMigratedConfigObjects,
  setMigrationResult,
  clearMigrationResults,
  setSelectedObjects,
  toggleSelectedObject,
  clearSelectedObjects,
  setSelectedObjectType,
  clearAllData,
  setDataMigrationStatus,
  clearDataMigrationStatus,
  setConnectionDetails,
  resetUserAttribute,
  setDiscoveryCompleted,
} = migrationSlice.actions;

export default migrationSlice.reducer;
